<template>
  <div class="aed-date-picker">
    <v-menu
      ref="menu1"
      v-model="menu1"
      transition="scale-transition"
      offset-y
      max-width="290px"
      min-width="auto"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          v-model="dateFormatted"
          :label="label"
          persistent-hint
          prepend-icon="mdi-calendar"
          v-bind="field"
          append-icon="mdi-close"
          @click:append="clearDate"
          @change="handleDateInput"
          @keydown.esc="menu1 = false"
          @keydown.tab="menu1 = false"
          @keydown.shift.tab="menu1 = false"
          @click:prepend="menu1 = true"
          @blur="validateDateFormatted"
          :error="!!errorMessage"
          :error-messages="errorMessage"
          v-on="on"
        />
      </template>
      <v-date-picker
        v-model="date"
        no-title
        @change="pickDate"
        :first-day-of-week="1"
        :max="maxDateForPicker"
        :min="minDateForPicker"
      />
    </v-menu>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'aedAutoDatePicker',
  props: {
    field: {
      type: Object,
      default: () => ({})
    },
    label: String,
    value: String, // Gebunden über v-model
  },
  data() {
    return {
      date: null,          // Internes Format für v-date-picker (YYYY-MM-DD)
      dateFormatted: '',   // Anzeigeformat für v-text-field (DD.MM.YYYY)
      menu1: false,
      errorMessage: '',
    };
  },
  computed: {
    isMaxDaysSet() {
      const maxDays = this.field && this.field.MAXDAYS;
      return maxDays !== null && maxDays !== undefined && maxDays !== '' && !isNaN(parseFloat(maxDays)) && isFinite(maxDays);
    },
    maxDateForPicker() {
      if (this.isMaxDaysSet) {
        return moment().add(parseInt(this.field.MAXDAYS, 10), 'days').format('YYYY-MM-DD');
      }
      return undefined;
    },
    minDateForPicker() {
      if (this.isMaxDaysSet) {
        return moment().format('YYYY-MM-DD');
      }
      return undefined;
    }
  },
  watch: {
    // Reagiert auf v-model Änderungen von außen
    value: {
      handler(newVal) {
        this.syncInternalValues(newVal);
      },
      immediate: true
    },
    // Fallback: Reagiert, wenn das field-Objekt geladen wird (und v-model leer ist)
    'field.VALUE': {
      handler(newVal) {
        if (!this.value && newVal) {
          this.syncInternalValues(newVal);
        }
      },
      immediate: true
    }
  },
  created() {
    // Initialer Check beim Erstellen
    const initialVal = this.value || (this.field ? this.field.VALUE : null);
    if (initialVal) {
      this.syncInternalValues(initialVal);
    }
  },
  methods: {
    syncInternalValues(val) {
      if (!val) {
        this.date = null;
        this.dateFormatted = '';
        return;
      }

      // Versuche das Datum aus verschiedenen Formaten zu lesen
      const mDate = moment(val, ['DD.MM.YYYY', 'YYYY-MM-DD'], true);
      
      if (mDate.isValid()) {
        this.date = mDate.format('YYYY-MM-DD');
        this.dateFormatted = mDate.format('DD.MM.YYYY');
        
        // Falls der Wert von field.VALUE kam, v-model im Parent aktualisieren
        if (val !== this.value) {
          this.$emit('input', this.dateFormatted);
        }
      }
    },

    handleDateInput() {
      this.dateFormatted = this.completeDate(this.dateFormatted);
      this.validateDateFormatted();
    },

    completeDate(input) {
      if (!input) return '';
      const parts = input.split('.');
      const currentDate = new Date();
      
      let day = parts[0] || currentDate.getDate().toString().padStart(2, '0');
      let month = parts[1] || (currentDate.getMonth() + 1).toString().padStart(2, '0');
      let year = parts[2] || currentDate.getFullYear().toString();

      if (day.length === 1) day = day.padStart(2, '0');
      if (month.length === 1) month = month.padStart(2, '0');
      if (year.length === 2) year = (parseInt(year) < 50 ? '20' : '19') + year;

      const formatted = `${day}.${month}.${year}`;
      return moment(formatted, 'DD.MM.YYYY', true).isValid() ? formatted : input;
    },

    validateDateFormatted() {
      this.errorMessage = '';
      const mDate = moment(this.dateFormatted, 'DD.MM.YYYY', true);

      if (!mDate.isValid()) {
        this.errorMessage = this.t ? this.t("ct", "STR_ERR_DATE") : 'Ungültiges Datum';
        return;
      }

      if (this.isMaxDaysSet) {
        const today = moment().startOf('day');
        const maxDate = moment().startOf('day').add(parseInt(this.field.MAXDAYS, 10), 'days');

        if (mDate.isBefore(today)) {
          this.errorMessage = this.t ? this.t("perf", "STR_ERR_DFDATLOW") : 'Datum liegt in der Vergangenheit';
        } else if (mDate.isAfter(maxDate)) {
          let msg = this.t ? this.t("listtplinc", "STR_ERR_DATERNG2") : 'Datum zu weit in der Zukunft (%d Tage)';
          this.errorMessage = msg.replace('%d', this.field.MAXDAYS);
        }
      }

      if (!this.errorMessage) {
        this.updateDate(this.dateFormatted);
      }
    },

    updateDate(newDateStr) {
      this.dateFormatted = newDateStr;
      const mDate = moment(newDateStr, 'DD.MM.YYYY');
      this.date = mDate.format('YYYY-MM-DD');
      this.$emit('input', this.dateFormatted);
    },

    pickDate() {
      if (this.date) {
        this.dateFormatted = moment(this.date, 'YYYY-MM-DD').format('DD.MM.YYYY');
        this.validateDateFormatted();
        this.menu1 = false;
      }
    },

    clearDate() {
      this.dateFormatted = '';
      this.date = null;
      this.errorMessage = '';
      this.$emit('input', '');
    }
  }
};
</script>

<style scoped>
.aed-date-picker {
  display: flex;
}
</style>